import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterType } from '@vgtv/api-client/lib/component/filtered_list';
import { Cancel as CancelIcon, Search as SearchIcon } from '@vgno/icons';

import { usePreviousValue } from '../../../utils/usePreviousValue';
import { Button } from '../../../Button/Button';

import { useDebounce } from './useDebounce';

import type { CnpStoryId } from '@vgtv/api-client/lib/cnp_story';
import type { CnpTagId } from '@vgtv/api-client/lib/cnp_tag';
import type { Filter } from '@vgtv/api-client/lib/component/filtered_list';
import type { FC } from 'react';

import * as styles from './SearchForm.module.scss';

const SEARCH_MIN_CHARACTERS = 3;
const SEARCH_DEBOUNCE_TIME = 500; // ms

function getEntity(filter: Filter) {
  switch (filter.type) {
    case FilterType.STORY:
      return filter.story;
    case FilterType.TAG:
      return filter.tag;
  }
}

export interface SearchFormProps {
  filters: Filter[];
  onChange: (q: {
    tagId: CnpTagId | null;
    storyId: CnpStoryId | null;
    query: string | null;
  }) => void;
  placeholder?: string;
  initiallyActiveFilter?: Filter;
  searchInputRef?: React.Ref<HTMLInputElement>;
  hideSearchField?: boolean;
  trackSearch: (query: string) => void;
}

// Fixme fix translations in this file and translate input aria label
const SearchForm: FC<SearchFormProps> = ({
  filters = [],
  onChange,
  placeholder,
  initiallyActiveFilter,
  searchInputRef,
  hideSearchField,
  trackSearch,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [activeFilter, setActiveFilter] = useState<Filter>();
  const { t } = useTranslation();

  // set the value in a side-effect, otherwise the callback won't trigger
  useEffect(() => {
    if (initiallyActiveFilter) {
      setActiveFilter(initiallyActiveFilter);
    }
  }, [initiallyActiveFilter]);

  const debouncedInputValue = useDebounce(inputValue, SEARCH_DEBOUNCE_TIME);

  const changes = useMemo(() => {
    const tagId =
      activeFilter && activeFilter.type === FilterType.TAG
        ? activeFilter.tag.id
        : null;

    const storyId =
      activeFilter && activeFilter.type === FilterType.STORY
        ? activeFilter.story.id
        : null;

    const query = debouncedInputValue.trim();

    return {
      query: query.length >= SEARCH_MIN_CHARACTERS ? query : null,
      tagId,
      storyId,
    };
  }, [debouncedInputValue, activeFilter]);

  const previousChanges = usePreviousValue(changes);

  const displayClearButton = activeFilter !== null;

  useEffect(() => {
    if (
      changes.tagId !== previousChanges.tagId ||
      changes.storyId !== previousChanges.storyId ||
      changes.query !== previousChanges.query
    ) {
      onChange(changes);
      if (changes.query) {
        trackSearch(changes.query);
      }
    }
  }, [changes, onChange, previousChanges, trackSearch]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setInputValue(value);
  };

  const onFilterClick = (filter: Filter) => () => {
    if (!isActiveFilter(filter)) {
      setActiveFilter(filter);
    }
  };

  const onClearButtonClick = () => {
    setActiveFilter(undefined);
  };

  function isActiveFilter(filter: Filter) {
    return (
      activeFilter &&
      activeFilter.type === filter.type &&
      getEntity(activeFilter).id === getEntity(filter).id
    );
  }

  return (
    <>
      {!hideSearchField && (
        <div className={styles.inputWrapper}>
          <div className={styles.searchIconWrapper}>
            <SearchIcon />
          </div>
          <input
            className={styles.searchInput}
            value={inputValue}
            type="search"
            aria-label="søk"
            placeholder={placeholder || t('Search')}
            onChange={onInputChange}
            ref={searchInputRef}
          />
        </div>
      )}
      <div className={styles.tagsList}>
        {filters.map((filter) => {
          const filterType = filter.type;

          const entity = getEntity(filter);
          const id = entity.id;
          const label = entity.title;

          const isActive = isActiveFilter(filter);

          return (
            <Button
              key={`${filterType}-${id}`}
              variant="contained"
              className={styles.tag}
              onClick={onFilterClick(filter)}
              active={isActive}
              disabled={isActive}
            >
              {label}
            </Button>
          );
        })}
        {filters.length > 0 && (
          <Button
            variant="text"
            disabled={!displayClearButton}
            onClick={onClearButtonClick}
            className={styles.clearButton}
          >
            <CancelIcon />
            {t('Clear filters')}
          </Button>
        )}
      </div>
    </>
  );
};

export { SearchForm };
